<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import OrderDataService from '@/services/OrderDataService'
import type Order from '@/types/Order'
import type ResponseData from '@/types/ResponseData'
import type Pagination from '@/types/Pagination'
import * as _ from 'lodash'
import OrderInvoiceDataService from '@/services/OrderInvoiceDataService'
import type OrderInvoice from '@/types/OrderInvoice'
import * as FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table as UiTable, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState, TableFilterDropdown } from '@/components/common/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { CircleCheck, Copy, Download, X } from 'lucide-vue-next'

export default defineComponent({
  name: 'order-list',
  components: {
    UiTable,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    TableFilterDropdown,
    Checkbox,
    Button,
    CircleCheck,
    Copy,
    Download,
    X,
  },
  props: {
    title: {
      type: String,
      default: undefined,
    },
    userId: {
      type: [String, Number],
      default: null,
    },
    isPaidOnly: {
      type: Boolean,
      default: false,
    },
    filterStartDate: {
      type: String,
      default: '',
    },
    filterEndDate: {
      type: String,
      default: '',
    },
  },
  emits: ['update:filter-start-date', 'update:filter-end-date'],
  data() {
    return {
      orders: [] as Order[],
      orderStatuses: [] as { id: number; description: string }[],
      singleOrderInvoice: {} as OrderInvoice,
      orderInvoices: [] as OrderInvoice[],
      currentOrder: {} as Order,
      router: useRouter(),
      searchTerm: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as Pagination,
      pagesRange: 5,
      selectedOrderId: null as number | null,
      selectedStatus: '',
      selectedOrderIds: [] as number[],
      bulkSelectedStatus: '',
      isBulkUpdating: false,
      localFilterStartDate: '',
      localFilterEndDate: '',
    }
  },
  methods: {
    formatDate(dateString: string) {
      const date = dayjs(dateString)
      return date.format('DD.MM.YYYY')
    },
    saveAs(filename: string, blob: Blob) {
      FileSaver.saveAs(blob, filename)
    },
    retrieveOrders(page: number, type: string) {
      this.selectedOrderIds = [];

      // If this is a paid-only view (order-profit page)
      if (this.isPaidOnly) {
        if (type === 'prev') page--;
        else if (type === 'next') page++;
        this.loaded = false;
        const filters = (this.localFilterStartDate && this.localFilterEndDate)
          ? { start_date: this.localFilterStartDate, end_date: this.localFilterEndDate }
          : undefined;
        OrderDataService.getPaidOrdersPaginated(page, filters)
          .then((response: ResponseData) => {
            this.orders = response.data.data.data || [];
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
        return;
      }
      // If this is a user-specific view, fetch only that user's orders
      if (this.userId) {
        if (type === 'prev') {
          page--;
        } else if (type === 'next') {
          page++;
        }
        this.loaded = false;
        OrderDataService.getAllOrdersForUser(String(this.userId), page)
          .then((response: ResponseData) => {
            this.orders = response.data.data.data || [];
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
        return;
      }
      // Unified search: search by ID, client, company name, and filter by status
      const searchValue = this.searchTerm;
      const statusValue = this.selectedStatus;
      console.log('[OrderTable] retrieveOrders called with:', searchValue, statusValue);
      this.loaded = false;
      // If searching by ID or text
      if (searchValue != null && searchValue !== 'all' && searchValue !== '') {
        const searchTerm = searchValue.trim();
        if (/^\d+$/.test(searchTerm)) {
          // Search by order ID (numeric)
          OrderDataService.getOrderById(Number(searchTerm))
            .then((response: ResponseData) => {
              let order = response.data.data;
              // Filter by status if selected
              if (order && statusValue && String(order.status) !== statusValue) order = null;
              this.orders = order ? [order] : [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              if (!order) {
                toast.info('Objednávka s týmto ID nebola nájdená.');
              }
            })
            .catch(() => {
              this.orders = [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              toast.info('Objednávka s týmto ID nebola nájdená.');
            });
        } else {
          // Search by client or company name
          OrderDataService.searchOrders(searchTerm)
            .then((response: ResponseData) => {
              let orders = response.data.data.data || [];
              // Filter by status if selected
              if (statusValue) {
                orders = orders.filter((o: Order) => String(o.status) === statusValue);
              }
              this.orders = orders;
              this.pagination = response.data.data || { current_page: 1, last_page: 1 };
              this.loaded = true;
              if (!this.orders.length) {
                toast.info('Žiadne objednávky neboli nájdené pre tento výraz.');
              }
            })
            .catch(() => {
              this.orders = [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              toast.info('Žiadne objednávky neboli nájdené pre tento výraz.');
            });
        }
      } else if (searchValue === undefined) {
        this.orders = [];
        this.loaded = true;
      } else {
        if (type === 'prev') {
          page--;
        } else if (type === 'next') {
          page++;
        }
        OrderDataService.getAllOrders(page, 20, statusValue)
          .then((response: ResponseData) => {
            const orders = response.data.data.data;
            this.orders = orders;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
      }
    },
    refreshList() {
      this.retrieveOrders(1, '')
      this.currentOrder = {} as Order
    },
    downloadZFA(id: number) {
      const order = this.orders.find((value: Order) => value.id == id);
      if (order && order.amount > 0) {
        OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 1)
          .then((response: ResponseData) => {
            this.singleOrderInvoice = response.data.data
            OrderInvoiceDataService.generateInvoiceById(this.singleOrderInvoice.id).then((response: { data: string }) => {
              const byteCharacters = atob(response.data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: 'application/pdf' })

              this.saveAs(this.singleOrderInvoice.file_name, blob)
            })
          })
          .catch((e: Error) => {
            toast.error('Sťahovanie zlyhalo: ' + e.message)
          })

      } else {
        toast.error('Sťahovanie nie je možné, FA neexistuje.');
      }

    },
    downloadFA(id: number) {
      const order = this.orders.find((value: Order) => value.id == id);
      if (order && order.amount > 0) {
        OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 2)
          .then((response: ResponseData) => {
            this.singleOrderInvoice = response.data.data
            OrderInvoiceDataService.generateInvoiceById(this.singleOrderInvoice.id).then((response: { data: string }) => {
              const byteCharacters = atob(response.data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: 'application/pdf' })

              this.saveAs(this.singleOrderInvoice.file_name, blob)
            })
          })
          .catch((e: Error) => {
            toast.error('Sťahovanie zlyhalo: ' + e.message)
          })

      } else {
        toast.error('Sťahovanie nie je možné, FA neexistuje.');
      }

    },
    getOrderStatusName(statusId: number) {
      if (!statusId) return '-';
      const found = this.orderStatuses.find((s) => String(s.id) === String(statusId));
      return found ? found.description : statusId;
    },
    copyOrderId(id: number) {
      navigator.clipboard.writeText(String(id));
      this.selectedOrderId = id;
      setTimeout(() => { this.selectedOrderId = null }, 1000);
      toast.success('ID objednávky skopírované!');
    },
    getStatusBadgeClass(statusId: number) {
      // Example: color code by status
      switch (statusId) {
        case 1: return 'inline-block px-2 py-1 rounded-sm bg-green-100 text-green-800 text-xs font-semibold';
        case 2: return 'inline-block px-2 py-1 rounded-sm bg-yellow-100 text-yellow-800 text-xs font-semibold';
        case 3: return 'inline-block px-2 py-1 rounded-sm bg-red-100 text-red-800 text-xs font-semibold';
        default: return 'inline-block px-2 py-1 rounded-sm bg-gray-100 text-gray-800 text-xs font-semibold';
      }
    },
    clearAllFilters() {
      // Reset both filters and trigger watcher
      if (this.searchTerm !== '' || this.selectedStatus !== '') {
        this.searchTerm = '';
        this.selectedStatus = '';
        // Watchers will trigger retrieveOrders automatically
      }
    },
    orderMatchesSearch(order: Order, searchTerm: string) {
      if (!order || !searchTerm) return false;
      // Check company_name field and also company property if present
      if (order.company_name && order.company_name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      // Try to check company.name if present (for search API)
      if ((order as any).company && (order as any).company.name && (order as any).company.name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      return false;
    },
    getCompanyName(order: any) {
      if (!order) return '—';
      if (order.company_name && typeof order.company_name === 'string' && order.company_name.trim() !== '') {
        return order.company_name;
      }
      if (order.company && typeof order.company.name === 'string' && order.company.name.trim() !== '') {
        return order.company.name;
      }
      return '—';
    },
    allVisibleOrdersSelected() {
      if (!this.orders?.length) return false;
      return this.orders.every((order: Order) => this.selectedOrderIds.includes(order.id));
    },
    toggleSelectAllVisible(checked: boolean) {
      if (checked) {
        this.selectedOrderIds = this.orders.map((order: Order) => order.id);
      } else {
        this.selectedOrderIds = [];
      }
    },
    toggleOrderSelection(orderId: number, checked: boolean) {
      if (checked) {
        if (!this.selectedOrderIds.includes(orderId)) {
          this.selectedOrderIds.push(orderId);
        }
      } else {
        this.selectedOrderIds = this.selectedOrderIds.filter((id) => id !== orderId);
      }
    },
    async applyBulkStatusUpdate() {
      if (!this.selectedOrderIds.length) {
        toast.error('Chyba: Vyberte aspoň jednu objednávku.');
        return;
      }

      if (!this.bulkSelectedStatus) {
        toast.error('Chyba: Vyberte stav pre hromadnú zmenu.');
        return;
      }

      this.isBulkUpdating = true;
      try {
        const statusId = Number(this.bulkSelectedStatus);
        const response = await OrderDataService.bulkUpdateStatus(this.selectedOrderIds, statusId);
        const updatedCount = response?.data?.data?.updated_count ?? this.selectedOrderIds.length;
        const alreadyInStatusCount = response?.data?.data?.already_in_status_count ?? 0;

        toast.success(`Stav bol úspešne zmenený pre ${updatedCount} objednávok.`);
        if (alreadyInStatusCount > 0) {
          toast.info(`${alreadyInStatusCount} objednávok už malo tento stav.`);
        }

        this.selectedOrderIds = [];
        this.retrieveOrders(this.pagination.current_page || 1, '');
      } catch (e: any) {
        toast.error('Chyba: Hromadná zmena stavu zlyhala.');
      } finally {
        this.isBulkUpdating = false;
      }
    },
  },
  computed: {
    searchValue() {
      return this.searchTerm;
    },
    canBulkStatusUpdate() {
      return !this.userId && !this.isPaidOnly;
    },
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);
      return _.range(startPage, endPage + 1);
    },
    statusOptions() {
      return [
        { value: '', label: 'Všetky stavy' },
        ...this.orderStatuses.map((s) => ({ value: String(s.id), label: s.description })),
      ]
    },
    bulkStatusOptions() {
      return [
        { value: '', label: 'Hromadná zmena stavu' },
        ...this.orderStatuses.map((s) => ({ value: String(s.id), label: s.description })),
      ]
    },
  },
  watch: {
    searchTerm() {
      this.retrieveOrders(1, '');
    },
    selectedStatus() {
      this.retrieveOrders(1, '');
    },
    filterStartDate(val: string) {
      this.localFilterStartDate = val;
      this.retrieveOrders(1, '');
    },
    filterEndDate(val: string) {
      this.localFilterEndDate = val;
      this.retrieveOrders(1, '');
    },
  },
  mounted() {
    this.localFilterStartDate = this.filterStartDate;
    this.localFilterEndDate = this.filterEndDate;
    OrderDataService.getOrderStatuses()
      .then((response) => {
        this.orderStatuses = response.data.data;
      })
      .catch(() => {
        this.orderStatuses = [];
      })
      .then(() => {
        this.retrieveOrders(1, '');
      });
  }
})

</script>

<template>
  <main class="flex-1 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <div>
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <div class="py-2">
          <div class="flex flex-col gap-4">
            <TableCard :title="title">
              <template #controls>
                <div v-if="!isPaidOnly" class="flex flex-col lg:flex-row gap-4 w-full">
                  <div class="relative flex-1 w-full">
                    <TableSearch v-model="searchTerm" placeholder="Hľadať podľa ID, klienta, spoločnosti alebo popisu..." />
                  </div>
                  <div class="relative w-full lg:w-64">
                    <TableFilterDropdown
                      v-if="statusOptions.length > 0"
                      :options="statusOptions"
                      :model-value="selectedStatus"
                      @update:model-value="selectedStatus = $event"
                    />
                  </div>
                  <div class="flex items-center w-full lg:w-auto">
                    <Button
                      variant="outline"
                      class="w-full lg:w-auto"
                      :disabled="!searchTerm && !selectedStatus"
                      @click="clearAllFilters"
                    >
                      <X />
                      <span>Vymazať filtre</span>
                    </Button>
                  </div>

                  <div v-if="canBulkStatusUpdate" class="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                    <TableFilterDropdown
                      :options="bulkStatusOptions"
                      :model-value="bulkSelectedStatus"
                      @update:model-value="bulkSelectedStatus = $event"
                    />
                    <Button
                      class="w-full sm:w-auto"
                      :disabled="!selectedOrderIds.length || !bulkSelectedStatus || isBulkUpdating"
                      @click="applyBulkStatusUpdate"
                    >
                      <CircleCheck />
                      <span>{{ isBulkUpdating ? 'Ukladám...' : `Zmeniť stav (${selectedOrderIds.length})` }}</span>
                    </Button>
                  </div>
                </div>
              </template>
              <div class="max-w-full overflow-x-auto min-w-0">
                <UiTable class="min-w-full divide-y divide-gray-100 dark:divide-gray-800 text-[13px]">
                  <TableHeader class="bg-gray-50 dark:bg-white/3 sticky top-0 z-10">
                    <TableRow>
                      <TableCell is-header v-if="canBulkStatusUpdate" scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <Checkbox
                          :checked="allVisibleOrdersSelected()"
                          @change="toggleSelectAllVisible"
                          :disabled="!orders?.length"
                          aria-label="Vybrať všetky"
                        />
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center gap-1"
                          :class="{ 'text-brand-500': selectedColumn == 'id' }">ID
                          <button class="ml-1 text-gray-400 hover:text-brand-500 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" aria-hidden="true"
                              fill="currentColor" viewBox="0 0 320 512">
                              <path
                                d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                            </svg>
                          </button>
                        </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center gap-1"
                          :class="{ 'text-brand-500': selectedColumn == 'name' }">
                          Klient
                          <button class="ml-1 text-gray-400 hover:text-brand-500 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" aria-hidden="true"
                              fill="currentColor" viewBox="0 0 320 512">
                              <path
                                d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                            </svg>
                          </button>
                        </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center gap-1"
                          :class="{ 'text-brand-500': selectedColumn == 'company' }">
                          Spoločnosť
                          <button class="ml-1 text-gray-400 hover:text-brand-500 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" aria-hidden="true"
                              fill="currentColor" viewBox="0 0 320 512">
                              <path
                                d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                            </svg>
                          </button>
                        </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center"> Popis </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center"> Stav objednávky </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center"> Uhradené? </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-6 py-2 font-medium text-left text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        <span class="inline-flex items-center gap-1"
                          :class="{ 'text-brand-500': selectedColumn == 'date' }">
                          Dátum vytvorenia
                          <button class="ml-1 text-gray-400 hover:text-brand-500 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" aria-hidden="true"
                              fill="currentColor" viewBox="0 0 320 512">
                              <path
                                d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                            </svg>
                          </button>
                        </span>
                      </TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-right text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        Celková suma</TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-right text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        Zal. faktúra</TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-right text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        Faktúra</TableCell>
                      <TableCell is-header scope="col"
                        class="px-2 py-2 font-medium text-right text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs bg-white sticky right-0 z-20 shadow-lg dark:bg-white/3">
                        Upraviť</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                    <TableRow v-for="order in orders" :key="order.id"
                      :class="['hover:bg-brand-50/70', selectedOrderId === order.id ? 'bg-brand-100/80 ring-2 ring-brand-200' : '', 'transition-colors']">
                      <TableCell v-if="canBulkStatusUpdate" class="px-2 py-2 text-center">
                        <Checkbox
                          :checked="selectedOrderIds.includes(order.id)"
                          @change="(checked) => toggleOrderSelection(order.id, checked)"
                          name="order-select"
                          aria-label="Vybrať objednávku"
                        />
                      </TableCell>
                      <TableCell class="px-2 py-2 font-medium text-gray-800 dark:text-white/90 flex items-center gap-1 whitespace-nowrap">
                        <span>{{ order.id }}</span>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          class="text-gray-400 hover:text-brand-500"
                          title="Kopírovať ID"
                          aria-label="Kopírovať ID"
                          @click="copyOrderId(order.id)"
                        >
                          <Copy class="size-4" />
                        </Button>
                      </TableCell>
                      <TableCell class="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[110px] whitespace-nowrap"
                        :title="order.first_name + ' ' + order.last_name">
                        {{ order.first_name }} {{ order.last_name ? order.last_name : order.company_id }}
                      </TableCell>
                      <TableCell class="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[110px] whitespace-nowrap"
                        :title="getCompanyName(order)">
                        {{ getCompanyName(order) }}
                      </TableCell>
                      <TableCell class="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[120px] whitespace-nowrap"
                        :title="order.description">
                        {{ order.description }}
                      </TableCell>
                      <TableCell class="px-2 py-2">
                        <span
                          :class="getStatusBadgeClass(order.status) + ' shadow-xs px-3 py-1 rounded-full text-xs font-semibold'">{{
                            getOrderStatusName(order.status) }}</span>
                      </TableCell>

                      <TableCell class="px-2 py-2 text-gray-700 dark:text-gray-300 text-center">
                        <FormKit type="checkbox" disabled :model-value="Boolean(Number(order.is_paid))" name="is_paid" id="is_paid"
                          :classes="{ input: 'mt-1 focus:ring-brand-500 focus:border-brand-500 block shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-900 text-brand-500' }" />
                      </TableCell>
                      <TableCell class="px-2 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ formatDate(order.created_at ? order.created_at.toString() : order.updated_at.toString()) }}</TableCell>
                      <TableCell class="px-2 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap font-medium">{{ Number(order.amount).toFixed(2) }} €</TableCell>
                      <TableCell class="px-2 py-2 font-medium text-right">
                        <Button variant="outline" size="xs" @click="downloadZFA(order.id)">
                          <Download class="size-4" />
                          <span>Stiahnuť</span>
                        </Button>
                      </TableCell>
                      <TableCell class="px-2 py-2 font-medium text-right">
                        <Button variant="outline" size="xs" @click="downloadFA(order.id)">
                          <Download class="size-4" />
                          <span>Stiahnuť</span>
                        </Button>
                      </TableCell>
                      <TableCell class="px-2 py-2 font-medium text-right bg-white sticky right-0 z-10 shadow-lg dark:bg-white/3">
                        <Button as-child variant="outline" size="xs">
                          <router-link :to="'/order-detail/' + order.id">Upraviť</router-link>
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableEmptyState
                      v-if="loaded && !orders?.length"
                      title="Žiadne objednávky neboli nájdené"
                      hint="Skúste upraviť hľadanie alebo filtre."
                      :colspan="canBulkStatusUpdate ? 12 : 11"
                    />
                  </TableBody>
                </UiTable>
          </div>
          <template #footer>
            <TablePagination
              v-if="pagination.last_page > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.last_page"
              @page-change="retrieveOrders($event, '')"
            />
          </template>
        </TableCard>
      </div>
    </div>
  </div>
</div>
</main>
</template>
